/**
 * Fetch wrapper for the Analyzer API, shared by both apps.
 * Injects the Supabase Bearer token on authenticated requests.
 */
import { getAccessToken } from './auth'

// VITE_API_URL conventions differ between apps: the frontend sets the bare
// origin (https://api.earthbenders.pt) while atlas sets it with the /api/v1
// suffix (its map api layer expects that). Normalize by stripping a trailing
// slash and a trailing /api/v1 so apiFetch can always append /api/v1 exactly
// once, instead of producing .../api/v1/api/v1/...
export const API_URL = (import.meta.env.VITE_API_URL ?? '')
  .replace(/\/+$/, '')
  .replace(/\/api\/v1$/, '')

/**
 * Authenticated fetch, injects Authorization header from the Supabase session.
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    const token = await getAccessToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const url = path.startsWith('http') ? path : `${API_URL}/api/v1${path}`

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({ detail: response.statusText }))
      const message = body.detail || body.message || `HTTP ${response.status}`
      return { data: null, error: new Error(message) }
    }

    if (response.status === 204) {
      return { data: null, error: null }
    }

    const data = await response.json()
    return { data: data as T, error: null }
  } catch (err) {
    return { data: null, error: err as Error }
  }
}

/**
 * Unauthenticated fetch (for public endpoints like products).
 */
export async function publicFetch<T = unknown>(
  path: string,
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const url = path.startsWith('http') ? path : `${API_URL}/api/v1${path}`
    const response = await fetch(url)

    if (!response.ok) {
      const body = await response.json().catch(() => ({ detail: response.statusText }))
      return { data: null, error: new Error(body.detail || response.statusText) }
    }

    const data = await response.json()
    return { data: data as T, error: null }
  } catch (err) {
    return { data: null, error: err as Error }
  }
}
